import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Loader2, CreditCard, CalendarClock, Clock, XCircle, CheckCircle2 } from 'lucide-react';

/**
 * Backlog fee confirmation for statement uploads - same policy/UX as the
 * open-banking connect backlog popup. Shows the ACCURATE parsed counts.
 *
 * iter1230 - Pricing V2: dialog is shown even when the backlog is fully
 * covered by the tier's free lifetime allowance, so the user understands
 * why they are (or aren't) being charged and explicitly clicks to proceed.
 *
 * Props: open, job { jobId, filename, billing }, onConfirm(mode), onCancel, busy
 */
export const UploadBacklogFeeDialog = ({ open, job, onConfirm, onCancel, busy }) => {
  const [choice, setChoice] = useState(null);
  if (!job) return null;
  const b = job.billing || {};
  const bp = job.bridgePreview || null;
  const symbol = b.symbol || '$';
  const fee = (b.total_fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  // iter1246-h · Fee credit (auto-issued when a paid upload was rolled
  // back). Keyed to this bank account — applied automatically at charge.
  const creditAvailable = Math.min(b.credit_available || 0, b.total_fee || 0);
  const feeDue = b.fee_due_after_credit != null
    ? b.fee_due_after_credit
    : Math.max(0, (b.total_fee || 0) - creditAvailable);
  const feeDueFmt = feeDue.toLocaleString(undefined, { minimumFractionDigits: 2 });
  const creditFmt = creditAvailable.toLocaleString(undefined, { minimumFractionDigits: 2 });
  // Fully-free: whole backlog is covered by the lifetime allowance / admin waiver
  const fullyFree = !!b.fully_free || (b.total_fee || 0) <= 0;
  const coveredByCredit = !fullyFree && creditAvailable > 0 && feeDue <= 0;
  const adminWaived = !!b.admin_waived;
  // iter1238 Phase 8.8 (2026-02-17) — preview-stage branch removed.
  // The pre-parse pdfplumber estimator produced wildly inaccurate
  // counts on real PDFs, so it was killed backend-side. Jobs now
  // always reach this dialog with ACCURATE post-parse counts.
  const isStalePreview = !!b.preview_stage;

  const act = (mode) => { setChoice(mode); onConfirm(mode); };

  if (isStalePreview) {
    // Historical job from before Phase 8.8 (preview-stage killed). Never
    // show its wrong estimates. Prompt the user to cancel and re-upload.
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel?.(false); }}>
        <DialogContent className="max-w-lg" data-testid="upload-backlog-fee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-slate-500" />
              Upload needs to be restarted
            </DialogTitle>
            <DialogDescription>
              This upload was paused with an older estimation flow that we&apos;ve since
              retired for accuracy. Please cancel this one and re-upload the file — the
              new flow will parse straight through and show you the exact backlog fee
              (usually within a minute).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              onClick={() => onCancel?.(true)}
              disabled={busy}
              className="w-full gap-2 bg-slate-700 hover:bg-slate-800"
              data-testid="backlog-cancel-upload-btn"
            >
              <XCircle className="w-4 h-4" /> Cancel and re-upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel?.(false); }}>
      <DialogContent className="max-w-lg" data-testid="upload-backlog-fee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {fullyFree
              ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              : <CalendarClock className="w-5 h-5 text-amber-500" />}
            {fullyFree ? 'Confirm backlog import' : 'Backlog processing fee'}
          </DialogTitle>
          <DialogDescription>
            &quot;{job.filename}&quot; includes transactions from before the month you joined AkountFly.
            {fullyFree && (
              <span className="block mt-1 text-emerald-700">
                {(b.already_in_books_count || 0) > 0 && (b.billable_backlog_count || 0) === 0
                  ? 'All backlog transactions in this statement are already in your books. No new charge applies.'
                  : `Good news — this backlog is covered by your${adminWaived ? ' admin waiver' : ' free lifetime allowance'} and won't be charged.`}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          className={`rounded-lg border p-4 space-y-2 text-sm ${
            fullyFree
              ? 'border-emerald-200 bg-emerald-50'
              : 'border-amber-200 bg-amber-50'
          }`}
          data-testid="upload-backlog-fee-breakdown"
        >
          {/* Phase 8.7 alias for automation contract */}
          <span data-testid="backlog-pricing-breakdown" className="sr-only">breakdown</span>
          {/*
            iter1238 Phase 8.13n — the top line now shows the TRUE count of
            rows whose dates land inside the backlog window
            (chargeable + bank charges within the window), so users don't
            see "Backlog transactions: 74" while "Chargeable transactions"
            also reads 74 with no visible link between them. The next two
            rows break the total down into "bank charges (free)" and
            "chargeable" — the numbers on those rows sum back to the top.
          */}
          <div className="flex justify-between">
            <span className="text-slate-600">Backlog transactions ({b.window_start} → {b.window_end})</span>
            <strong data-testid="backlog-count">
              <span data-testid="backlog-billable-count">{(b.backlog_count || 0) + (b.excluded_bank_charge_count || 0)}</span>
            </strong>
          </div>
          {(b.filtered_before_start_date || 0) > 0 && (
            <div className="flex justify-between" data-testid="upload-backlog-excluded-pre-start-row">
              <span className="text-slate-600">
                Before accounting start
                {b.window_start && (
                  <span className="ml-1 text-[11px] text-slate-400">
                    dated before {String(b.window_start).slice(0, 10)} · not billed
                  </span>
                )}
              </span>
              <strong className="text-slate-500">
                <span data-testid="backlog-excluded-pre-start-count">{b.filtered_before_start_date}</span>
              </strong>
            </div>
          )}
          {(b.excluded_bank_charge_count || 0) > 0 && (
            <div className="flex justify-between pl-4" data-testid="upload-backlog-charges-row">
              <span className="text-slate-600">— Bank charges (free)<span className="ml-1 text-[11px] text-slate-400">excluded from backlog fee</span></span>
              <strong data-testid="backlog-charges-count">
                <span data-testid="backlog-excluded-charges-count">{b.excluded_bank_charge_count}</span>
              </strong>
            </div>
          )}
          {(b.chargeable ?? 0) > 0 && (
            <div className="flex justify-between pl-4" data-testid="upload-backlog-chargeable-row">
              <span className="text-slate-600">— Chargeable<span className="ml-1 text-[11px] text-slate-400">counted for the fee</span></span>
              <strong>{b.chargeable}</strong>
            </div>
          )}
          {/* iter1241 · Rows already in your bank feed from a previous upload.
              Shown ONLY when we detected any — otherwise line is silent so
              first-time uploads don't see confusing zero-copy. */}
          {(b.already_in_books_count || 0) > 0 && (
            <div className="flex justify-between pl-4" data-testid="upload-backlog-already-in-books-row">
              <span className="text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Already in your bank feed
                <span className="ml-1 text-[11px] text-emerald-600/80">no re-charge — you paid for these before</span>
              </span>
              <strong className="text-emerald-700" data-testid="backlog-already-in-books-count">
                {b.already_in_books_count}
              </strong>
            </div>
          )}
          {(b.post_registration_count || 0) > 0 && (
            <div className="flex justify-between" data-testid="upload-post-registration-row">
              <span className="text-slate-600">Current transactions (free)<span className="ml-1 text-[11px] text-slate-400">from registration month onward</span></span>
              <strong data-testid="post-registration-count">{b.post_registration_count}</strong>
            </div>
          )}
          {/* Fallback for old jobs stamped before Phase 4 - just show free_count as-is */}
          {!(b.excluded_bank_charge_count || b.post_registration_count) && (
            <div className="flex justify-between"><span className="text-slate-600">Current transactions (free)</span><strong>{b.free_count || 0}</strong></div>
          )}
          {b.free_available > 0 && (
            <div className="flex justify-between" data-testid="upload-backlog-free-row">
              <span className="text-slate-600">
                Covered by your free lifetime allowance
                <span className="ml-1 text-[11px] text-slate-400">({b.free_available} lifetime grant · {b.lifetime_remaining_after ?? Math.max(0, (b.free_available || 0) - (b.free_consumed || 0))} remaining after this upload)</span>
              </span>
              <strong className="text-emerald-700">- {b.free_consumed || 0}</strong>
            </div>
          )}
          <div className="flex justify-between"><span className="text-slate-600">Rate per backlog transaction</span><strong>{symbol}{b.per_transaction_rate}</strong></div>
          {b.minimum_fee > 0 && (
            <div className="flex justify-between text-[11px] text-slate-500">
              <span>Minimum charge (per bank)</span>
              <strong>{symbol}{b.minimum_fee}</strong>
            </div>
          )}
          <div className={`flex justify-between border-t pt-2 text-base ${fullyFree ? 'border-emerald-200' : 'border-amber-200'}`}>
            <span className="font-medium text-slate-700">One-time backlog fee</span>
            <strong
              className={fullyFree ? 'text-emerald-700' : 'text-amber-700'}
              data-testid="backlog-fee-amount"
            >
              <span data-testid="backlog-total-amount">
                {fullyFree ? 'Free' : `${symbol}${fee}`}
              </span>
            </strong>
          </div>
          {creditAvailable > 0 && !fullyFree && (
            <>
              <div className="flex justify-between text-emerald-700" data-testid="backlog-credit-line">
                <span>Fee credit from your previous upload</span>
                <strong>- {symbol}{creditFmt}</strong>
              </div>
              <div className="flex justify-between border-t border-emerald-200 pt-2 text-base">
                <span className="font-semibold text-slate-800">You pay today</span>
                <strong className="text-emerald-700" data-testid="backlog-due-after-credit">
                  {feeDue <= 0 ? `${symbol}0.00 — fully covered` : `${symbol}${feeDueFmt}`}
                </strong>
              </div>
            </>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Bank charges are excluded from the count. Transactions from your registration month onward are always free.
          AI classification on backlog transactions is included (you are not billed twice).
          {b.tier && b.free_available > 0 && (
            <span className="ml-1">Your <strong className="uppercase">{b.tier}</strong> plan has {b.free_available} more backlog transactions on your lifetime allowance.</span>
          )}
        </p>

        {/* iter1242 · Bridge-verification preview for connected-account
            rescue uploads. Only rendered when the target bank is Mono/
            Plaid AND the upload sits inside a gap (mid_period_bridge)
            or entirely outside provider coverage (pre_provider_backfill/
            tail_gap). Anchors are computed by the backend on `bridge_preview`. */}
        {bp && bp.kind === 'mid_period_bridge' && (
          <div
            className="rounded-lg border p-3 text-xs space-y-1.5 border-sky-200 bg-sky-50"
            data-testid="upload-bridge-preview"
          >
            <div className="font-semibold text-sky-900 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Bridge check (filling an Open-Banking gap)
            </div>
            {/* Opening anchor */}
            {bp.pre_bridge_ok === true && (
              <div className="text-emerald-800" data-testid="bridge-open-ok">
                ✓ Opens at <strong>{symbol}{(bp.manual_opening_expected || 0).toLocaleString()}</strong>
                {' '}— matches the closing balance on{' '}
                <strong>{bp.pre_gap_last_row_date}</strong> from your provider feed.
              </div>
            )}
            {bp.pre_bridge_ok === false && (
              <div className="text-red-700" data-testid="bridge-open-mismatch">
                ✗ Opens at <strong>{symbol}{(bp.manual_opening_expected ?? '?').toLocaleString?.() ?? bp.manual_opening_expected}</strong>
                {' '}but the provider feed closes at{' '}
                <strong>{symbol}{(bp.pre_gap_close_derived ?? '?').toLocaleString?.() ?? bp.pre_gap_close_derived}</strong>{' '}
                on <strong>{bp.pre_gap_last_row_date}</strong>. Statement will be blocked after confirm.
              </div>
            )}
            {(bp.pre_bridge_ok === null || bp.pre_bridge_ok === undefined) && (
              <div className="text-amber-700" data-testid="bridge-open-unverifiable">
                ⚠ Your statement doesn&apos;t print an opening balance we can compare
                {bp.pre_gap_last_row_date && (
                  <> against the provider close on <strong>{bp.pre_gap_last_row_date}</strong></>
                )}. We&apos;ll still verify internally, but the two-sided balance parity check can&apos;t run.
              </div>
            )}
            {/* Closing anchor */}
            {bp.post_bridge_ok === true && (
              <div className="text-emerald-800" data-testid="bridge-close-ok">
                ✓ Closes at <strong>{symbol}{(bp.manual_closing_expected || 0).toLocaleString()}</strong>
                {' '}— matches the balance your provider resumed with on{' '}
                <strong>{bp.post_gap_first_row_date}</strong>.
              </div>
            )}
            {bp.post_bridge_ok === false && (
              <div className="text-red-700" data-testid="bridge-close-mismatch">
                ✗ Closes at <strong>{symbol}{(bp.manual_closing_expected ?? '?').toLocaleString?.() ?? bp.manual_closing_expected}</strong>
                {' '}but the provider expected{' '}
                <strong>{symbol}{(bp.post_gap_open_derived ?? '?').toLocaleString?.() ?? bp.post_gap_open_derived}</strong>{' '}
                on <strong>{bp.post_gap_first_row_date}</strong>. Statement will be blocked after confirm.
              </div>
            )}
            {(bp.post_bridge_ok === null || bp.post_bridge_ok === undefined) && (
              <div className="text-amber-700" data-testid="bridge-close-unverifiable">
                ⚠ Your statement doesn&apos;t print a closing balance we can compare
                {bp.post_gap_first_row_date && (
                  <> against the provider resume on <strong>{bp.post_gap_first_row_date}</strong></>
                )}.
              </div>
            )}
            {(bp.pre_bridge_ok === false || bp.post_bridge_ok === false) && (
              <div className="text-[11px] text-slate-500 italic pt-1 border-t border-sky-200">
                You can still confirm — nothing is charged until every check passes.
                If the bridge fails, the upload will be cancelled cleanly and your card will not be charged.
              </div>
            )}
          </div>
        )}
        {bp && bp.kind === 'pre_provider_backfill' && (
          <div
            className="rounded-lg border p-3 text-xs border-slate-200 bg-slate-50"
            data-testid="upload-bridge-preview-backfill"
          >
            <span className="font-semibold text-slate-800">Pre-provider backfill.</span>{' '}
            This statement covers <strong>{bp.new_period_start} → {bp.new_period_end}</strong>,
            which is entirely older than any transaction your Open Banking connection has pulled
            (from <strong>{bp.provider_first_date}</strong>). Safe to import; no overlap with the provider feed.
          </div>
        )}
        {bp && bp.kind === 'tail_gap' && (
          <div
            className="rounded-lg border p-3 text-xs border-slate-200 bg-slate-50"
            data-testid="upload-bridge-preview-tail"
          >
            <span className="font-semibold text-slate-800">Tail-gap rescue.</span>{' '}
            Your provider feed last covered <strong>{bp.provider_last_date}</strong>. This
            statement ({bp.new_period_start} → {bp.new_period_end}) picks up after that —
            balance continuity will be verified against the previous close.
          </div>
        )}
        {!fullyFree && (
          <p className="text-[11px] text-slate-500 italic" data-testid="backlog-charge-timing-note">
            You&apos;ll only be charged once we&apos;ve fully verified your statement is safe to record. If any check fails, the upload is cancelled and your card is never charged.
          </p>
        )}

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button
            onClick={() => act('pay_backlog')}
            disabled={busy}
            className={`w-full gap-2 ${fullyFree ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#108981] hover:bg-[#0D746D]'}`}
            data-testid="backlog-pay-btn"
          >
            <span data-testid="confirm-backlog-btn" className="contents">
              {busy && choice === 'pay_backlog'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : (fullyFree ? <CheckCircle2 className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />)}
              {fullyFree
                ? `Confirm — covered by free allowance (${symbol}0)`
                : coveredByCredit
                  ? `Confirm — covered by your ${symbol}${creditFmt} credit (${symbol}0 to pay)`
                  : creditAvailable > 0
                    ? `Confirm ${symbol}${feeDueFmt} (after ${symbol}${creditFmt} credit) & process everything`
                    : `Confirm ${symbol}${fee} & process everything`}
            </span>
          </Button>
          {/* iter1242 · The "Process current only (skip backlog)" option
              was removed intentionally. Silently dropping the backlog
              rows creates an inconsistent opening balance (dated
              accounting_start_date−1 but valued at registration-month
              close) and leaves an untraceable equity anchor. If a user
              doesn't want to pay backlog now they can defer with
              "Try later" or lower their `accounting_start_date` on
              Business Settings so the rows are no longer in backlog. */}
          <Button variant="secondary" onClick={() => onCancel?.(false)} disabled={busy} className="w-full gap-2" data-testid="backlog-try-later-btn">
            <Clock className="w-4 h-4" />
            Try later — I&apos;ll decide from Bank Accounts
          </Button>
          <Button variant="ghost" onClick={() => onCancel?.(true)} disabled={busy} className="w-full gap-2 text-red-500 hover:text-red-600" data-testid="backlog-cancel-upload-btn">
            <span data-testid="cancel-backlog-btn" className="contents">
              <XCircle className="w-4 h-4" /> Cancel this upload
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadBacklogFeeDialog;
